// «مقارنة بالسوق» on a property's page: similar properties of the company around it (same purpose and category,
// same type when there are enough), price per m² — this one against the middle of the market — and the nearest ones.
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { Alert, Box, Chip, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { tr } from '../lib/i18n';
import { fmtNum, money } from '../lib/labels';
import { SectionCard } from './org';

interface Comp {
  id: string;
  ref_no: string;
  status: string;
  price: number | null;
  size: number | null;
  ppm: number | null;
  type_name: string;
  rooms: number | null;
  area_name: string | null;
  distanceKm: number | null;
  approx: boolean;
}
interface Market {
  basis: 'radius' | 'area' | 'none';
  approx?: boolean;
  radiusKm?: number | null;
  sameType?: boolean;
  currency: string;
  count: number;
  medianPrice?: number | null;
  medianPpm?: number | null;
  p25Ppm?: number | null;
  p75Ppm?: number | null;
  myPpm?: number | null;
  diffPct?: number | null;
  percentile?: number | null;
  comps: Comp[];
}

const r0 = (n: number | null | undefined) => (n == null ? '—' : fmtNum(Math.round(n)));
const dist = (d: number | null) => (d == null ? '—' : d < 1 ? `${Math.round(d * 1000)} ${tr('م', 'm')}` : `${d.toFixed(1)} ${tr('كم', 'km')}`);

/** where this property's price per m² sits between the cheaper and dearer quarter of the market */
function RangeBar({ p25, p75, median, mine }: { p25: number; p75: number; median: number; mine: number }) {
  const lo = Math.min(p25, mine) * 0.92;
  const hi = Math.max(p75, mine) * 1.08;
  const at = (v: number) => `${((v - lo) / (hi - lo)) * 100}%`;
  return (
    <Box sx={{ position: 'relative', height: 44, mt: 1.5, mb: 0.5 }} dir="ltr">
      <Box sx={{ position: 'absolute', top: 18, left: 0, right: 0, height: 8, borderRadius: 4, bgcolor: 'action.hover' }} />
      <Box sx={{ position: 'absolute', top: 18, left: at(p25), width: `calc(${at(p75)} - ${at(p25)})`, height: 8, borderRadius: 4, bgcolor: 'primary.light', opacity: 0.55 }} />
      <Tooltip title={tr('الوسيط', 'Median')}>
        <Box sx={{ position: 'absolute', top: 12, left: at(median), width: 2, height: 20, bgcolor: 'text.secondary', transform: 'translateX(-1px)' }} />
      </Tooltip>
      <Tooltip title={tr('هذا العقار', 'This property')}>
        <Box sx={{ position: 'absolute', top: 10, left: at(mine), width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', border: '3px solid #fff', boxShadow: 2, transform: 'translateX(-12px)' }} />
      </Tooltip>
      <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', top: 30, left: at(p25), transform: 'translateX(-50%)' }}>{r0(p25)}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', top: 30, left: at(p75), transform: 'translateX(-50%)' }}>{r0(p75)}</Typography>
    </Box>
  );
}

export function MarketCard({ propertyId }: { propertyId: string }) {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['property', propertyId, 'market'], queryFn: () => api<Market>(`/properties/${propertyId}/market`), staleTime: 60_000 });
  const m = q.data;

  const basis = !m ? '' : m.basis === 'radius'
    ? tr(`ضمن ${m.radiusKm} كم`, `Within ${m.radiusKm} km`)
    : m.basis === 'area' ? tr('في نفس المنطقة', 'In the same area') : '';

  return (
    <SectionCard
      title={tr('مقارنة بالسوق', 'Market comparison')}
      action={m && m.basis !== 'none' ? (
        <Stack direction="row" spacing={0.75}>
          {basis && <Chip size="small" variant="outlined" label={basis} />}
          <Chip size="small" variant="outlined" label={m.sameType ? tr('نفس النوع', 'Same type') : tr('نفس الفئة', 'Same category')} />
        </Stack>
      ) : undefined}
    >
      {q.isLoading ? <Skeleton variant="rounded" height={140} /> : !m || m.basis === 'none' ? (
        <Typography color="text.secondary" variant="body2">
          {tr('حدّد موقع العقار على الخريطة (أو منطقته) لنقارنه بالعقارات القريبة.', 'Set the property’s location (or its area) to compare it with nearby properties.')}
        </Typography>
      ) : m.count < 3 ? (
        <Typography color="text.secondary" variant="body2">
          {tr(`لا يوجد عدد كافٍ من العقارات المشابهة القريبة للمقارنة (${m.count}).`, `Not enough similar properties nearby to compare (${m.count}).`)}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {m.approx && (
            <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
              {tr('موقع هذا العقار تقريبي (حسب منطقته) — حدّد موقعه الدقيق لمقارنة أدق.', 'This property’s location is approximate (its area) — pin it exactly for a sharper comparison.')}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">{tr('سعر المتر لهذا العقار', 'This property per m²')}</Typography>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h5" fontWeight={700}>{r0(m.myPpm)}</Typography>
                <Typography variant="body2" color="text.secondary">{m.currency}/{tr('م²', 'm²')}</Typography>
                {m.diffPct != null && (
                  <Chip
                    size="small"
                    icon={<InsightsOutlinedIcon />}
                    color={Math.abs(m.diffPct) < 5 ? 'default' : m.diffPct < 0 ? 'success' : 'warning'}
                    label={Math.abs(m.diffPct) < 5
                      ? tr('ضمن سعر السوق', 'At market price')
                      : m.diffPct < 0
                        ? tr(`أقل من السوق ${Math.abs(m.diffPct)}%`, `${Math.abs(m.diffPct)}% below market`)
                        : tr(`أعلى من السوق ${m.diffPct}%`, `${m.diffPct}% above market`)}
                  />
                )}
              </Stack>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">{tr(`الوسيط لـ ${m.count} عقار مشابه`, `Median of ${m.count} similar`)}</Typography>
              <Typography variant="h6" fontWeight={600}>{r0(m.medianPpm)} <Typography component="span" variant="body2" color="text.secondary">{m.currency}/{tr('م²', 'm²')}</Typography></Typography>
              <Typography variant="caption" color="text.secondary">{tr('وسيط السعر', 'Median price')}: {money(m.medianPrice != null ? Math.round(m.medianPrice) : null, m.currency)}</Typography>
            </Box>
          </Stack>
          {m.p25Ppm != null && m.p75Ppm != null && m.medianPpm != null && m.myPpm != null && (
            <Box>
              <RangeBar p25={m.p25Ppm} p75={m.p75Ppm} median={m.medianPpm} mine={m.myPpm} />
              <Typography variant="caption" color="text.secondary">
                {tr(`الشريط: نصف العقارات المشابهة سعر مترها بين ${r0(m.p25Ppm)} و${r0(m.p75Ppm)}. هذا العقار أغلى من ${m.percentile}% منها.`, `The band holds the middle half of similar properties (${r0(m.p25Ppm)}–${r0(m.p75Ppm)}). This one is dearer per m² than ${m.percentile}% of them.`)}
              </Typography>
            </Box>
          )}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tr('العقار', 'Property')}</TableCell>
                  <TableCell align="right">{tr('المساحة', 'Size')}</TableCell>
                  <TableCell align="right">{tr('السعر', 'Price')}</TableCell>
                  <TableCell align="right">{tr('للمتر', 'Per m²')}</TableCell>
                  <TableCell align="right">{tr('المسافة', 'Distance')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {m.comps.map((c) => (
                  <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => nav(`/properties/${c.id}`)}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{c.type_name}{c.rooms != null ? ` · ${c.rooms} ${tr('غرف', 'rooms')}` : ''}</Typography>
                      <Typography variant="caption" color="text.secondary" dir="ltr">{c.ref_no}</Typography>
                    </TableCell>
                    <TableCell align="right">{c.size != null ? `${fmtNum(c.size)} m²` : '—'}</TableCell>
                    <TableCell align="right">{fmtNum(c.price)}</TableCell>
                    <TableCell align="right">{r0(c.ppm)}</TableCell>
                    <TableCell align="right">{dist(c.distanceKm)}{c.approx ? ' ≈' : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      )}
    </SectionCard>
  );
}
